import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

const CaseMedicalDetail = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: '项目概览' },
    { id: 'comparison', label: '前后对比' },
    { id: 'features', label: '核心功能' },
    { id: 'implementation', label: '实施过程' }
  ];

  const comparisonData = [
    {
      metric: '病历查询速度',
      before: '平均5-10分钟',
      after: '3-5秒',
      improvement: '提升99.2%',
      icon: 'ri-time-line'
    },
    {
      metric: '文档处理效率',
      before: '每天200份',
      after: '每天1200份',
      improvement: '提升500%',
      icon: 'ri-file-text-line'
    },
    {
      metric: '数据安全等级',
      before: '基础防护',
      after: '军用级加密',
      improvement: '安全性提升10倍',
      icon: 'ri-shield-check-line'
    },
    {
      metric: '存储成本',
      before: '年费120万',
      after: '年费30万',
      improvement: '节省75%',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      metric: '医生工作效率',
      before: '每患者15分钟',
      after: '每患者8分钟',
      improvement: '提升47%',
      icon: 'ri-user-heart-line'
    },
    {
      metric: '系统可用性',
      before: '95.2%',
      after: '99.9%',
      improvement: '提升4.7%',
      icon: 'ri-server-line'
    }
  ];

  const coreFeatures = [
    {
      title: '智能病历识别',
      description: 'AI自动识别和分类各类医疗文档，支持手写病历OCR识别',
      benefits: ['识别准确率99.5%', '处理速度提升20倍', '支持50+文档类型'],
      icon: 'ri-brain-line'
    },
    {
      title: '多级安全防护',
      description: '端到端加密、权限分级管理、操作日志追踪',
      benefits: ['256位AES加密', '零数据泄露事故', '符合HIPAA标准'],
      icon: 'ri-shield-keyhole-line'
    },
    {
      title: '智能检索系统',
      description: '基于AI的语义搜索，支持模糊查询和关联推荐',
      benefits: ['秒级检索响应', '智能关联推荐', '多维度筛选'],
      icon: 'ri-search-2-line'
    },
    {
      title: '移动端同步',
      description: '医生可随时随地安全访问患者病历信息',
      benefits: ['实时数据同步', '离线模式支持', '多设备兼容'],
      icon: 'ri-smartphone-line'
    }
  ];

  const timeline = [
    { week: '第1-2周', phase: '需求调研', status: 'completed', description: '深入了解医院现有系统和业务流程' },
    { week: '第3-4周', phase: '系统设计', status: 'completed', description: '制定技术架构和安全方案' },
    { week: '第5-8周', phase: '核心开发', status: 'completed', description: '开发病历管理和安全模块' },
    { week: '第9-10周', phase: '数据迁移', status: 'completed', description: '安全迁移历史病历数据' },
    { week: '第11-12周', phase: '系统测试', status: 'completed', description: '全面测试系统功能和安全性' },
    { week: '第13-14周', phase: '用户培训', status: 'completed', description: '培训医护人员使用新系统' },
    { week: '第15周', phase: '正式上线', status: 'completed', description: '系统正式投入使用' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">项目背景</h3>
                <p className="text-gray-600 mb-6">
                  某三甲医院拥有2000+床位，日门诊量超过8000人次。传统纸质病历管理面临存储空间不足、
                  查询效率低下、安全风险高等问题，急需数字化转型。
                </p>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">项目规模</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 覆盖科室：35个临床科室</li>
                    <li>• 用户数量：1200+医护人员</li>
                    <li>• 病历数量：500万+份历史病历</li>
                    <li>• 日处理量：3000+份新增病历</li>
                  </ul>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">面临挑战</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-1">
                      <i className="ri-close-line text-red-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">效率低下</h4>
                      <p className="text-gray-600 text-sm">病历查找耗时长，影响诊疗效率</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-1">
                      <i className="ri-close-line text-red-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">安全隐患</h4>
                      <p className="text-gray-600 text-sm">纸质病历易丢失、损坏，存在泄露风险</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-1">
                      <i className="ri-close-line text-red-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">成本高昂</h4>
                      <p className="text-gray-600 text-sm">存储空间、人工管理成本持续上升</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-1">
                      <i className="ri-close-line text-red-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">合规风险</h4>
                      <p className="text-gray-600 text-sm">难以满足日益严格的医疗数据保护要求</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">解决方案</h3>
              <p className="text-gray-600 mb-6">
                基于AI和云计算技术，构建智能化电子病历管理平台，实现病历数字化存储、
                智能检索、安全管理和移动访问的全流程数字化管理。
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-cpu-line text-green-600 text-2xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900">AI智能化</h4>
                  <p className="text-gray-600 text-sm">自动识别分类，智能检索推荐</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shield-check-line text-blue-600 text-2xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900">安全可靠</h4>
                  <p className="text-gray-600 text-sm">多层加密，权限管控，审计追踪</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-smartphone-line text-purple-600 text-2xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900">移动便捷</h4>
                  <p className="text-gray-600 text-sm">随时随地安全访问患者信息</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'comparison':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">效率与安全性显著提升</h3>
              <p className="text-gray-600">通过数字化转型，医院在多个关键指标上实现了显著改善</p>
            </div>

            <div className="grid gap-6">
              {comparisonData.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className={`${item.icon} text-blue-600 text-xl`}></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{item.metric}</h4>
                        <div className="flex items-center space-x-6 mt-2">
                          <div>
                            <span className="text-sm text-gray-500">改进前：</span>
                            <span className="text-red-600 font-medium">{item.before}</span>
                          </div>
                          <i className="ri-arrow-right-line text-gray-400"></i>
                          <div>
                            <span className="text-sm text-gray-500">改进后：</span>
                            <span className="text-green-600 font-medium">{item.after}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        {item.improvement}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-6">投资回报分析</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">800万</div>
                  <div className="text-blue-100">初始投资</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">300万</div>
                  <div className="text-blue-100">年节省成本</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">2.7年</div>
                  <div className="text-blue-100">投资回收期</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">275%</div>
                  <div className="text-blue-100">5年ROI</div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-white/10 rounded-lg">
                <p className="text-sm">
                  <strong>成本节省来源：</strong>减少纸质存储成本90万/年，提高医生效率节省人力成本120万/年，
                  降低管理成本60万/年，减少合规风险成本30万/年。
                </p>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">核心功能模块</h3>
              <p className="text-gray-600">四大核心功能模块，全面提升医院病历管理效率和安全性</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${feature.icon} text-white text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h4>
                      <p className="text-gray-600 mb-4">{feature.description}</p>
                      <div className="space-y-2">
                        {feature.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center">
                              <i className="ri-check-line text-green-600 text-xs"></i>
                            </div>
                            <span className="text-sm text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">技术架构优势</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-cloud-line text-blue-600 text-2xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">云原生架构</h4>
                  <p className="text-gray-600 text-sm">弹性扩展，高可用性，支持海量数据处理</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-database-2-line text-green-600 text-2xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">分布式存储</h4>
                  <p className="text-gray-600 text-sm">数据冗余备份，99.99%可用性保障</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-lock-line text-purple-600 text-2xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">零信任安全</h4>
                  <p className="text-gray-600 text-sm">端到端加密，动态权限控制</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'implementation':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">15周完整实施过程</h3>
              <p className="text-gray-600">从需求调研到正式上线，每个阶段都有明确的目标和交付成果</p>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <div key={index} className="relative flex items-start space-x-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center relative z-10">
                      <i className="ri-check-line text-green-600 text-xl"></i>
                    </div>
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">{item.phase}</h4>
                        <span className="text-sm text-gray-500">{item.week}</span>
                      </div>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">项目团队构成</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">技术团队</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-blue-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">项目经理 × 1</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-code-line text-blue-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">高级开发工程师 × 4</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-shield-line text-blue-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">安全专家 × 2</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-test-tube-line text-blue-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">测试工程师 × 2</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">医院团队</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-heart-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">信息科主任 × 1</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="ri-stethoscope-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">科室主任 × 5</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="ri-nurse-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">护士长 × 8</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="ri-admin-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">系统管理员 × 3</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/b951bee02cb68cbac96edbea68d8d27e.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              某医疗机构电子病历智能管理平台
            </h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              通过AI驱动的智能化病历管理系统，显著提升医院运营效率和文档安全性
            </p>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">99.2%</div>
                <div className="text-blue-100">查询速度提升</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">500%</div>
                <div className="text-blue-100">处理效率提升</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">300万</div>
                <div className="text-blue-100">年节省成本</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderTabContent()}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4">
            想了解更多医疗数字化解决方案？
          </h2>
          <p className="text-xl mb-8">
            我们的专家团队将为您提供定制化的医疗信息化咨询服务
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap">
              预约咨询
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap">
              下载案例详情
            </button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CaseMedicalDetail;
